import { useCallback, useMemo, useState } from "react";
import { Badge, Button, Flex, Table, Text } from "@radix-ui/themes";
import { PlusIcon } from "@radix-ui/react-icons";
import { Loader2Icon } from "lucide-react";
import { useQuery, useQueryClient } from "react-query";
import { Search } from "@/components/shared/inputs/search";
import { toast } from "sonner";
import { useGdprEventApi } from "@/v2/entities/gdpr-event/GdprEvent.api";
import {
  CreateGdprEventRequest,
  GetTenantGdprEventsResponse,
  TenantGdprEventStatusResponse,
} from "@/v2/entities/gdpr-event/GdprEvent.model";
import { CreateGdprEventDialog } from "./CreateGdprEventDialog";
import styles from "./Gdpr.module.scss";

const GDPR_EVENTS_QUERY_KEY = "gdpr-events";

function formatDate(dateValue: string): string {
  return new Date(dateValue).toLocaleString();
}

function getStatusColor(status: string): "orange" | "green" | "gray" {
  if (status === "IN_PROGRESS") {
    return "orange";
  }

  if (status === "SUCCESS") {
    return "green";
  }

  return "gray";
}

export function Gdpr() {
  const queryClient = useQueryClient();
  const gdprEventApi = useGdprEventApi();
  const [searchPhrase, setSearchPhrase] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);

  const { data: events = [], isLoading, isFetching } = useQuery<GetTenantGdprEventsResponse>({
    queryKey: [GDPR_EVENTS_QUERY_KEY],
    queryFn: () => gdprEventApi.getTenantGdprEvents(),
    refetchInterval: (queryData) => {
      const currentEvents = (queryData ?? []) as TenantGdprEventStatusResponse[];
      const hasInProgressEvent = currentEvents.some(
        (event) => event.status === "IN_PROGRESS"
      );

      return hasInProgressEvent ? 5000 : false;
    },
  });

  const filteredEvents = useMemo(() => {
    if (!searchPhrase) {
      return events;
    }

    const normalizedSearch = searchPhrase.toLowerCase();
    return events.filter((event) => {
      return (
        event.eventId.toLowerCase().includes(normalizedSearch) ||
        event.initiatedBy.toLowerCase().includes(normalizedSearch) ||
        event.status.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [events, searchPhrase]);

  const sortedEvents = useMemo(() => {
    return filteredEvents
      .slice()
      .sort(
        (a, b) =>
          new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime()
      );
  }, [filteredEvents]);

  const showInitialLoader = useMemo(() => {
    return isLoading && events.length === 0;
  }, [events.length, isLoading]);

  const handleCreateEvent = useCallback(
    async (request: CreateGdprEventRequest) => {
      setIsCreatingEvent(true);
      try {
        const response = await gdprEventApi.createGdprEvent(request);
        toast.success(
          `GDPR event created. ${response.numberOfTickets} tickets selected for anonymization.`
        );
        await queryClient.invalidateQueries([GDPR_EVENTS_QUERY_KEY]);
        return true;
      } catch {
        toast.error("Failed to create GDPR event");
        return false;
      } finally {
        setIsCreatingEvent(false);
      }
    },
    [gdprEventApi, queryClient]
  );

  const handleOpenCreateDialog = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  const handleCreateDialogOpenChange = useCallback((open: boolean) => {
    setCreateDialogOpen(open);
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Text as="div" size="4" weight="medium">
          GDPR
        </Text>
      </div>

      <div className={styles.content}>
        <Flex direction="column" gap="8">
          <Flex direction="row" gap="2" justify="between" align="center">
            <Flex direction="row" gap="2" align="center">
              <Flex gap="2" align="center">
                <Text size="3" weight="medium">
                  GDPR Events
                </Text>
                <Badge color="gray">{filteredEvents.length}</Badge>
              </Flex>
              <Search
                initialValue={searchPhrase}
                placeholder="Search events"
                setSearchPhrase={setSearchPhrase}
                debounce={50}
              />
            </Flex>
            <Button
              color="gray"
              variant="solid"
              highContrast
              onClick={handleOpenCreateDialog}
            >
              <PlusIcon />
              <Text as="span" size="2" weight="medium">
                Create Event
              </Text>
            </Button>
          </Flex>

          {showInitialLoader ? (
            <Flex justify="center" align="center">
              <Loader2Icon className={styles.loadingIcon} />
            </Flex>
          ) : (
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>
                    <Text as="span" size="2" color="gray">
                      Event ID
                    </Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    <Text as="span" size="2" color="gray">
                      Date Range
                    </Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    <Text as="span" size="2" color="gray">
                      Status
                    </Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    <Text as="span" size="2" color="gray">
                      Progress
                    </Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>
                    <Text as="span" size="2" color="gray">
                      Initiated By
                    </Text>
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sortedEvents.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={5}>
                      <Flex
                        align="center"
                        justify="center"
                        py="6"
                        direction="column"
                        gap="2"
                      >
                        <Text size="2" color="gray">
                          {events.length === 0
                            ? "No GDPR events found"
                            : "No GDPR events match your search"}
                        </Text>
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                ) : null}
                {sortedEvents.map((event) => {
                  const processedTickets =
                    event.totalTicketsToAnonymize - event.ticketsLeftToAnonymize;
                  const progressLabel = `${processedTickets}/${event.totalTicketsToAnonymize}`;

                  return (
                    <Table.Row key={event.eventId}>
                      <Table.Cell>
                        <Text size="1" color="gray" className={styles.eventId}>
                          {event.eventId}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex direction="column" gap="1">
                          <Text size="2">{formatDate(event.fromDate)}</Text>
                          <Text size="2" color="gray">
                            to {formatDate(event.toDate)}
                          </Text>
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          color={getStatusColor(event.status)}
                          className={styles.statusBadge}
                        >
                          {event.status}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" color="gray">
                          {progressLabel}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" color="gray">
                          {event.initiatedBy}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>
          )}

          {isFetching && !showInitialLoader ? (
            <Text size="1" color="gray">
              Refreshing events...
            </Text>
          ) : null}
        </Flex>
      </div>

      <CreateGdprEventDialog
        open={createDialogOpen}
        onOpenChange={handleCreateDialogOpenChange}
        onCreate={handleCreateEvent}
        isSubmitting={isCreatingEvent}
      />
    </div>
  );
}
